import React, { useEffect, useState } from "react"
import { usePageContext } from "vike-react/usePageContext"
import { AdjustmentsHorizontalIcon, ChevronRightIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import Modal from "aldoo-ra/Modal"
import useT from "../../hooks/useT"
import { useCategories } from "../../hooks/useCatalogData"
import { childCategories, findCategory, hasProducts } from "../../utils/categories"
import { CategoryColumn, ContentTypeColumn } from "./CategoryColumn"

/**
 * The phone's stand-in for the category page's side column: a button naming
 * the category the page is on, which opens the column's lists (categories,
 * product types, each with a search) as a sheet sliding up from the bottom.
 * Picking one navigates, and the sheet closes with the page change; a tap on
 * the shade or the close mark closes it too. `label` and `value` name what
 * the button shows (a product type page names its type).
 */
export default function CategoryFilterSheet({ parentCategoryId, label, value }) {
  const t = useT()
  const { list } = useCategories()
  const { urlPathname } = usePageContext()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const current = parentCategoryId ? findCategory(list, parentCategoryId) : null
  // the sheet lists the category's own subcategories under its name; a
  // category with none gets its siblings or the roots, under "Categories"
  const hasSubs = !!current && childCategories(list, parentCategoryId).some(hasProducts)
  const sheetTitle = value || (hasSubs ? current.name : t`Categories`)

  // a pick navigates: the sheet goes with the page
  useEffect(() => setOpen(false), [urlPathname])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-3 h-12 px-4 rounded-full border border-bordered bg-paper text-left hover:border-text transition-colors"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="category-filter-button"
      >
        <AdjustmentsHorizontalIcon className="w-5 h-5 shrink-0 text-text" />
        <span className="flex-1 min-w-0 flex flex-col leading-tight">
          <span className="text-[12px] text-text_secondary">{label || t`Categories`}</span>
          <span className="text-[15px] font-semibold text-text truncate">{value || current?.name || t`All Products`}</span>
        </span>
        <ChevronRightIcon className="w-5 h-5 shrink-0 text-text_secondary" />
      </button>

      {/* the sheet's title is the modal's; the search stays fixed under it
          while the lists scroll */}
      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        variant="sf_sheet"
        title={sheetTitle}
        closeOnClickOutside
        closeOnEscape
        styleOverrides={{
          contentWrapper: "flex-1 min-h-0 flex flex-col overflow-hidden",
          content: "w-full flex-1 min-h-0 flex flex-col",
        }}
      >
        <div className="flex flex-col flex-1 min-h-0" data-testid="category-filter-sheet">
          <label className="relative block shrink-0 px-5 pt-2 pb-3">
            <MagnifyingGlassIcon className="absolute left-8 top-1/2 -translate-y-1/2 mt-[-2px] w-4 h-4 text-white/60 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t`Search`}
              className="w-full h-11 pl-9 pr-3 rounded-lg border border-white/20 bg-white/10 text-[16px] text-white placeholder:text-white/50 outline-none focus:border-primary"
              data-testid="category-filter-search"
            />
          </label>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-6 scrollbar-thin scrollbar-thumb-white/30 scrollbar-track-transparent">
            <CategoryColumn parentCategoryId={parentCategoryId} sheet filter={query} />
            <ContentTypeColumn sheet filter={query} />
          </div>
        </div>
      </Modal>
    </>
  )
}
